import React, { useEffect, memo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Sidebar from './Sidebar';
import MobileNavigation from './MobileNavigation';
import Footer from './Footer';
import { useAppStore } from '@/store';
import { Toaster } from "@/components/ui/toaster";
import { useNavigationType, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { LogIn, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useIsMobile } from '@/hooks/use-mobile';
import UserNotifications from '@/components/users/UserNotifications';
import { getNavItems } from '@/lib/navigation';
import BugReportModal from '@/components/feedback/BugReportModal';
import { useMessagingStore } from '@/store/useMessagingStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import UserMessaging from '@/components/users/UserMessaging';
import { useProfile } from '@/hooks/useProfile';

const MainNavigation = memo(() => {
  const isMobile = useIsMobile();
  const { user } = useAuth();

  const appLinks = [
    { to: '/', label: 'Home' },
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/models', label: 'Models' },
    { to: '/purchase', label: 'Training' },
    { to: '/research', label: 'Research' },
  ];

  const marketingLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/insights', label: 'Insights' },
    { to: '/news', label: 'News' },
    { to: '/purchase', label: 'Purchase' },
  ];

  const links = user ? appLinks : marketingLinks;
  const visibleLinks = isMobile ? links.slice(0, 3) : links;

  return (
    <div className="flex space-x-2">
      {visibleLinks.map((link) => (
        <Link key={link.to} to={link.to} className="px-3 py-2 text-sm rounded-md hover:bg-accent">
          {link.label}
        </Link>
      ))}
    </div>
  );
});
MainNavigation.displayName = 'MainNavigation';

const UserActions = () => {
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  if (!user) {
    return (
      <Button 
        variant="ghost"
        size="sm"
        onClick={() => navigate('/auth')}
        className="flex items-center gap-2"
      >
        <LogIn className="h-4 w-4" />
        <span>Login</span>
      </Button>
    );
  }

  const displayName = profile?.name || user.email?.split('@')[0] || 'User';
  const avatarUrl = profile?.avatar;
  const initials = displayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full">
          <Avatar className="w-8 h-8 bg-muted">
            <AvatarImage src={avatarUrl} alt={displayName} />
            <AvatarFallback className="bg-muted text-xs font-semibold text-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="font-medium">{displayName}</span>
            <span className="text-xs text-muted-foreground">{user.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/profile')}>
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/settings')}>
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const MainLayout = memo(({ children }: { children?: React.ReactNode }) => {
  const { darkMode } = useAppStore();
  const location = useLocation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const isEmbedded = typeof window !== 'undefined' && window.self !== window.top;
    return isEmbedded ? true : false;
  });
  const [showBugReport, setShowBugReport] = useState(false);
  const { activeMessagingUser, closeMessaging } = useMessagingStore();
  
  const isAuthPage = location.pathname === '/auth';
  const isMarketingPage = [
    '/research',
    '/mcp',
    '/link',
    '/reports',
    '/enterprise',
    '/partnership',
    '/maplept',
    '/mapleos',
    '/maplenode',
    '/credits',
    '/models',
    '/privacy',
    '/news',
    '/about',
    '/purchase',
    '/community',
    '/changelog',
    '/sovereign-ai-canada',
    '/ai-model-training',
    '/small-language-model-training',
    '/private-ai',
    '/pipeda-compliant-ai',
    '/healthcare-ai-canada',
    '/legal-ai-canada',
    '/canadian-ai-infrastructure',
    '/rag-vs-fine-tuning',
    '/data-residency',
    '/security',
    '/compliance',
    '/canadian-ai-platform',
    '/enterprise-ai',
    '/insights',
  ].some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));

  const isLandingPage = location.pathname === '/';

  let isRouterAvailable = true;
  try {
    useNavigationType();
  } catch (e) {
    isRouterAvailable = false;
    console.log('Router context not available, sidebar will not be shown');
  }

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  React.useEffect(() => {
    const root = document.documentElement;
    if (isMarketingPage || isLandingPage) {
      root.classList.add('allow-window-scroll');
    } else {
      root.classList.remove('allow-window-scroll');
    }
    return () => {
      root.classList.remove('allow-window-scroll');
    };
  }, [isMarketingPage, isLandingPage]);

  const navItems = React.useMemo(() => {
    return getNavItems(location.pathname, setShowBugReport).filter(item =>
      !['/about', '/news', '/community'].includes(item.path)
    );
  }, [location.pathname]);

  const mobileMenuItems = React.useMemo(() => {
    const allNavItems = getNavItems(location.pathname, setShowBugReport, true);
    return allNavItems.filter(item =>
      ['/purchase', '/models'].includes(item.path)
    );
  }, [location.pathname]);

  if (isAuthPage) {
    return (
      <div className="h-full w-full flex flex-col">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <main className="flex-1 overflow-auto flex flex-col">
          <div className="flex-1 overflow-auto">
            {children}
          </div>
        </main>
      </div>
    );
  }

  if (isMarketingPage || isLandingPage) {
    return (
      <div
        className="relative w-full min-h-[100dvh] bg-background z-[100]"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {children || <Outlet />}
        {showBugReport && <BugReportModal open={showBugReport} onOpenChange={setShowBugReport} />}
      </div>
    );
  }

  return (
    <div className="flex h-full w-full overflow-hidden bg-background h-[100dvh]">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {isRouterAvailable && !isMobile && (
        <Sidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
      )}

      <div className="flex flex-1 flex-col h-full overflow-hidden relative">
        <div className="flex-none h-14 bg-card flex justify-between items-center px-4 border-b z-10">
            <MainNavigation />
            <div className="flex items-center space-x-2">
              <UserNotifications />
              <UserActions />
            </div>
        </div>

        <main 
          className={`flex-1 overflow-y-auto overflow-x-hidden bg-background relative ${isMobile ? 'pb-20' : ''}`}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {children || <Outlet />} 
        </main>
        
        {!isMobile && (
          <div className="flex-none bg-card z-10">
            <Footer />
          </div>
        )}
      </div>
      
      {isRouterAvailable && isMobile && (
        <MobileNavigation
          navItems={navItems}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          UserActionsComponent={UserActions}
        />
      )}
      {showBugReport && <BugReportModal open={showBugReport} onOpenChange={setShowBugReport} />}

      {activeMessagingUser && (
        <Dialog open={!!activeMessagingUser} onOpenChange={closeMessaging}>
          <DialogContent className="sm:max-w-[500px] h-[600px] flex flex-col p-0">
            <DialogHeader className="py-3 px-6 border-b flex-shrink-0">
              <DialogTitle>Message {activeMessagingUser.name}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden">
              <UserMessaging
                userId={activeMessagingUser.id}
                userName={activeMessagingUser.name}
                onClose={closeMessaging}
                hideInternalHeader={true}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
});

MainLayout.displayName = 'MainLayout';

export default MainLayout;